import { useLocation } from 'react-router-dom'
import { Mail, MessagesSquare, Phone } from 'lucide-react'
import { Logo } from '@/components/layout/Logo'
import { InstagramBrandIcon, WhatsAppBrandIcon } from '@/components/icons/SocialBrandIcons'
import { StoreScrollLink } from '@/store/components/StoreScrollLink'
import { scrollToTop } from '@/lib/scrollToTop'
import { cn } from '@/lib/utils'

interface StoreFooterProps {
  instagram?: string
  email?: string
  phone?: string
  whatsapp?: string
  onRequestProduct?: () => void
  onRaiseBug?: () => void
  productRequestActive?: boolean
  raiseBugActive?: boolean
}

function exploreItemClass(active: boolean) {
  return cn(
    'transition-colors',
    active ? 'text-gold-300 font-medium' : 'text-zinc-400 hover:text-gold-300'
  )
}

function contactItemClass() {
  return 'inline-flex items-center gap-2 text-sm font-medium text-zinc-400 transition-colors active:text-gold-300'
}

function buildWhatsAppUrl(phone: string) {
  const digits = phone.replace(/\D/g, '')
  return `https://wa.me/${digits}?text=${encodeURIComponent('Hello Bazm-e-Musk, I would like to get in touch.')}`
}

export function StoreFooter({
  instagram,
  email,
  phone,
  whatsapp,
  onRequestProduct,
  onRaiseBug,
  productRequestActive = false,
  raiseBugActive = false,
}: StoreFooterProps) {
  const { pathname, search } = useLocation()
  const params = new URLSearchParams(search)

  const isShopAll = pathname === '/shop' && params.get('featured') !== '1'
  const isFeatured = pathname === '/shop' && params.get('featured') === '1'
  const isOrders = pathname === '/orders'
  const isContact = pathname === '/contact'
  const isRequestProduct =
    productRequestActive ||
    (pathname === '/search' && params.get('request') === '1')
  const isReturnPolicy = pathname === '/return-refund-policy'
  const isTerms = pathname === '/terms-and-conditions'
  const isPrivacy = pathname === '/privacy-policy'

  return (
    <footer className="mt-20 border-t border-luxury-700/50 bg-luxury-900/80 arabesque-pattern">
      <div className="store-container py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Logo variant="store" size="lg" showText={false} linkTo="/" />
          <p className="mt-4 max-w-sm text-sm text-zinc-500 leading-relaxed">
            Bazm-e-Musk crafts premium attars and oriental fragrances — timeless
            luxury in every drop.
          </p>
        </div>
        <div>
          <h4 className="font-display text-lg text-gold-300 mb-4">Explore</h4>
          <ul className="space-y-2 text-sm">
            <li>
              <StoreScrollLink
                to="/shop"
                aria-current={isShopAll ? 'page' : undefined}
                className={exploreItemClass(isShopAll)}
              >
                Shop All
              </StoreScrollLink>
            </li>
            <li>
              <StoreScrollLink
                to="/shop?featured=1"
                aria-current={isFeatured ? 'page' : undefined}
                className={exploreItemClass(isFeatured)}
              >
                Featured
              </StoreScrollLink>
            </li>
            <li>
              <StoreScrollLink
                to="/orders"
                aria-current={isOrders ? 'page' : undefined}
                className={exploreItemClass(isOrders)}
              >
                Track Order
              </StoreScrollLink>
            </li>
            <li>
              <StoreScrollLink
                to="/contact"
                aria-current={isContact ? 'page' : undefined}
                className={exploreItemClass(isContact)}
              >
                Contact Us
              </StoreScrollLink>
            </li>
            <li>
              {onRequestProduct ? (
                <button
                  type="button"
                  onClick={() => {
                    scrollToTop()
                    onRequestProduct()
                  }}
                  aria-current={isRequestProduct ? 'page' : undefined}
                  className={exploreItemClass(isRequestProduct)}
                >
                  Request a Product
                </button>
              ) : (
                <StoreScrollLink
                  to="/search?request=1"
                  aria-current={isRequestProduct ? 'page' : undefined}
                  className={exploreItemClass(isRequestProduct)}
                >
                  Request a Product
                </StoreScrollLink>
              )}
            </li>
            <li>
              {onRaiseBug ? (
                <button
                  type="button"
                  onClick={() => {
                    scrollToTop()
                    onRaiseBug()
                  }}
                  aria-current={raiseBugActive ? 'page' : undefined}
                  className={cn(exploreItemClass(raiseBugActive), 'text-left')}
                >
                  Raise a Bug &amp; Win a Discount Coupon
                </button>
              ) : (
                <StoreScrollLink to="/" className={exploreItemClass(false)}>
                  Raise a Bug &amp; Win a Discount Coupon
                </StoreScrollLink>
              )}
            </li>
            <li>
              <StoreScrollLink
                to="/return-refund-policy"
                aria-current={isReturnPolicy ? 'page' : undefined}
                className={exploreItemClass(isReturnPolicy)}
              >
                Return & Refund Policy
              </StoreScrollLink>
            </li>
            <li>
              <StoreScrollLink
                to="/terms-and-conditions"
                aria-current={isTerms ? 'page' : undefined}
                className={exploreItemClass(isTerms)}
              >
                Terms & Conditions
              </StoreScrollLink>
            </li>
            <li>
              <StoreScrollLink
                to="/privacy-policy"
                aria-current={isPrivacy ? 'page' : undefined}
                className={exploreItemClass(isPrivacy)}
              >
                Privacy Policy
              </StoreScrollLink>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-display text-lg text-gold-300 mb-4">Contact</h4>
          <ul className="space-y-2 text-sm">
            <li>
              <StoreScrollLink
                to="/contact"
                aria-current={isContact ? 'page' : undefined}
                className={contactItemClass()}
              >
                <MessagesSquare className="h-4 w-4 shrink-0" />
                Contact form
              </StoreScrollLink>
            </li>
            {email && (
              <li>
                <a href={`mailto:${email}`} className={contactItemClass()}>
                  <Mail className="h-4 w-4 shrink-0" />
                  {email}
                </a>
              </li>
            )}
            {phone && (
              <li>
                <a
                  href={`tel:${phone.replace(/\s/g, '')}`}
                  className={contactItemClass()}
                >
                  <Phone className="h-4 w-4 shrink-0" />
                  {phone}
                </a>
              </li>
            )}
            {whatsapp && (
              <li>
                <a
                  href={buildWhatsAppUrl(whatsapp)}
                  target="_blank"
                  rel="noreferrer"
                  className={contactItemClass()}
                >
                  <WhatsAppBrandIcon className="h-4 w-4 shrink-0" />
                  WhatsApp
                </a>
              </li>
            )}
            {instagram && (
              <li>
                <a
                  href={instagram}
                  target="_blank"
                  rel="noreferrer"
                  className={contactItemClass()}
                >
                  <InstagramBrandIcon className="h-4 w-4 shrink-0" />
                  Instagram
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div
        id="store-footer-credit"
        className="border-t border-luxury-800 py-6 text-center space-y-3"
      >
        <p className="text-xs text-zinc-600">
          © {new Date().getFullYear()} Bazm-e-Musk. All rights reserved.
        </p>
        <p className="inline-flex flex-wrap items-center justify-center gap-1.5 text-xs text-zinc-500">
          <span className="tracking-wide uppercase text-[10px] text-zinc-600">
            Designed &amp; Developed with
          </span>
          <span className="text-rose-400/90" aria-hidden>
            ❤️
          </span>
          <span className="text-zinc-600">by</span>
          <a
            href="https://www.nazarnadeem.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium gold-gradient-text underline-offset-4 transition-opacity hover:opacity-80 hover:underline"
          >
            Nazar Nadeem
          </a>
        </p>
      </div>
    </footer>
  )
}
